<template>
  <v-menu offset="2" max-height="400" :close-on-content-click="false">
    <template #activator="{ props: activatorProps }">
      <v-btn secondary variant="outlined" v-bind="activatorProps">
        {{ buttonTitle }}
        <v-icon
          role="img"
          aria-hidden="false"
          icon="rtls:BaseIconViewMenuOutline"
          color="primary"
          size="24"
          class="ml-1 columns-icon"
        />
      </v-btn>
    </template>

    <v-list lines="one">
      <draggable v-model="allColumns" :item-key="''" handle=".draggable" @end="onDragEnd">
        <template #item="{ element }">
          <v-list-item density="compact" height="48">
            <!-- this div is to align the icon and checkbox vertically, doesn't work out of the box in Vuetify 3 -->
            <div class="d-flex align-center">
              <v-icon
                class="draggable mr-2"
                size="24"
                :class="{ invisible: isColumnDisabled(element) }"
              >
                mdi-drag
              </v-icon>

              <v-checkbox
                v-model="selectedColumns"
                :value="getColumnIdentifier(element)"
                :label="getColumnLabel(element)"
                :disabled="isColumnDisabled(element)"
                hide-details
                class="rtls-checkbox mx-2"
                @update:model-value="toggleColumn(element)"
              />
            </div>
          </v-list-item>
        </template>
      </draggable>
    </v-list>
  </v-menu>
</template>

<script setup>
import { defineEmits, defineProps, ref, computed } from 'vue';
import cloneDeep from 'lodash/cloneDeep';

const props = defineProps({
  columns: {
    type: Array,
    required: true,
  },
  buttonTitle: {
    type: String,
    required: false,
    default: 'Columns',
  },
  disabledColumns: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const emit = defineEmits(['update:selected-columns', 'update:column-order']);

// to avoid mutating the original columns array without expicit emit
const allColumns = ref(cloneDeep(props.columns));

// to be flexible to different column types
const getColumnIdentifier = column => {
  return column && (column.id || column.key);
};

const getColumnLabel = column => {
  return column && (column.title || column.name);
};

const isColumnDisabled = computed(() => column => {
  const colIdentifier = getColumnIdentifier(column);
  return props.disabledColumns.includes(colIdentifier);
});

const selectedColumns = computed({
  get: () => allColumns.value.filter(col => col.visible).map(col => getColumnIdentifier(col)),
  set: newValue => {
    allColumns.value.forEach(col => {
      const colIdentifier = getColumnIdentifier(col);
      col.visible = newValue.includes(colIdentifier);
    });
  },
});

const onDragEnd = () => {
  emit('update:column-order', allColumns.value);
};

const toggleColumn = column => {
  emit('update:selected-columns', column);
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.invisible {
  visibility: hidden;
}

.columns-icon {
  color: $assortment-primary-colour !important;
  font-family: 'Material Icons Outlined';
  font-feature-settings: 'liga';
  font-size: 20px;
}
</style>
